// Crew avatar read helpers (web).
//
// Crew profile photos live in the private `crew-avatars` bucket, pointed at by
// crew_profiles.profile_photo. crew_profiles RLS is "own row only", so the
// producer dashboard can't read other users' pointers directly — it goes
// through the `get_crew_avatars(briefing)` RPC (SECURITY DEFINER, scoped to a
// briefing the caller can see), then signs the returned paths in one batch.
//
// Read paths should call these helpers rather than touching storage directly so
// the signed-URL plumbing stays in one place (mirrors festivalMap.ts).

import { CREW_AVATARS_BUCKET } from "@produce/shared";
import { supabase } from "@/integrations/supabase/client";

const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour

/** Map of crew member user_id → signed avatar URL for one briefing. */
export type CrewAvatarUrlMap = Map<string, string>;

/**
 * Fetch every crew avatar for a briefing and resolve them to signed URLs.
 * Returns an empty map when the briefing has no photos (or the caller can't
 * see it). Never throws for a partial failure — a missing photo just falls
 * back to initials in the UI.
 */
export async function fetchCrewAvatarUrls(briefingId: string): Promise<CrewAvatarUrlMap> {
  const result: CrewAvatarUrlMap = new Map();
  if (!briefingId) return result;

  const { data, error } = await supabase.rpc("get_crew_avatars", { p_briefing_id: briefingId });
  if (error) {
    console.warn("fetchCrewAvatarUrls: get_crew_avatars failed", error);
    return result;
  }

  const rows = (data ?? []).filter(
    (r): r is { user_id: string; profile_photo: string } => !!r?.user_id && !!r?.profile_photo,
  );
  if (rows.length === 0) return result;

  const { data: signed, error: signErr } = await supabase.storage
    .from(CREW_AVATARS_BUCKET)
    .createSignedUrls(
      rows.map((r) => r.profile_photo),
      SIGNED_URL_TTL_SECONDS,
    );
  if (signErr) {
    console.warn("fetchCrewAvatarUrls: signing failed", signErr);
    return result;
  }

  (signed ?? []).forEach((entry, i) => {
    if (entry.signedUrl && !entry.error) {
      result.set(rows[i].user_id, entry.signedUrl);
    }
  });
  return result;
}
